import React, { useContext } from "react";
import styled from "styled-components";
import { segmentedTabs } from "features/bots/BotForm/NewLook/styles";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context.ts";
import {
  BotFormType,
  Error,
  HedgingMode,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types.ts";
import HedgingSelector from "features/bots/BotForm/Entries/HedgingSelector.tsx";
import GridsSelector from "features/bots/BotForm/Strategies/GridsSelector.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces, OrderTypes } from "@shared/types";
import Strategies from "features/bots/BotForm/Strategies/Strategies.tsx";
import { HEDGING_FIXED_OPTION_ID, HEDGING_RANGE_OPTION_ID } from "testIds.ts";
import Tabs from "common/Tabs/Tabs.tsx";
import { isSymbol } from "@shared/types/utils.ts";
import { openMessage } from "utils/misc.tsx";

const HEDGING_RANGE_MODE_SYMBOL_WARNING =
  "The range hedging mode can only be selected when all the edited bots have the same symbol.";

const HedgingModeSelector = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const {
    botFormType,
    hedgingMode,
    symbols,
    setHedgingMode,
    removeError,
    toggleField,
  } = useContext(BotFormContext)!;
  const rangeModeDisabled =
    botFormType === BotFormType.BULK_EDIT &&
    hedgingMode === HedgingMode.FIXED &&
    isSymbol(symbols);

  const onHedgingModeChange = (value: HedgingMode) => {
    setHedgingMode(value);
    toggleField(ToggleField.ENTRIES, value === HedgingMode.RANGE);
    if (value === HedgingMode.FIXED) {
      removeError(Error.GRIDS_DATA);
    }
  };

  const tabsItems = [
    {
      key: HedgingMode.RANGE,
      label: (
        <span
          data-testid={HEDGING_RANGE_OPTION_ID}
          onClick={() => {
            if (rangeModeDisabled) {
              openMessage(HEDGING_RANGE_MODE_SYMBOL_WARNING, "warning");
            }
          }}
        >
          {t("labels.range")}
        </span>
      ),
      disabled: rangeModeDisabled,
      children: (
        <>
          <GridsSelector
            style={{ marginTop: 8, marginBottom: 16 }}
            updateGridsData={true}
            isGeneralTab={true}
            hideHedgingTitle={true}
            afterPriceRangeContent={<Strategies orderType={OrderTypes.ENTRY} />}
          />
        </>
      ),
      forceRender: true,
    },
    {
      key: HedgingMode.FIXED,
      label: (
        <span data-testid={HEDGING_FIXED_OPTION_ID}>{t("labels.fixed")}</span>
      ),
      children: <HedgingSelector hideTitle={true} />,
      forceRender: true,
    },
  ];

  return (
    <StyledTabs
      activeKey={isSymbol(hedgingMode) ? HedgingMode.RANGE : hedgingMode}
      onChange={(value) => onHedgingModeChange(value as HedgingMode)}
      items={tabsItems}
    />
  );
};

export default React.memo(HedgingModeSelector);

const StyledTabs = styled(Tabs)`
  .ant-tabs-tab {
    padding: 0 0 12px 0;
  }

  .ant-tabs-tab-disabled .ant-tabs-tab-btn {
    color: ${(p) => p.theme.colors.secondaryGrey} !important;
  }
  ${(p) => p.theme.isNewLook && segmentedTabs};
`;
